import { table_config } from './helper';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import WebDataTable from 'app/shared/datatable/web/datatable';
import DashboardSlidingNav from 'app/member/modules/tenants/tenants-sliding-nav';
import { Spacer } from 'app/layouts/generic';
import { TenantBoard } from './board';

import {
  addOneTenantToStore,
  removeOneTenantFromStore,
  loadTenantsToStore
} from 'store/actions/tenant';
import { setPageTitle } from 'store/actions/header';
import GmModal from 'app/shared/modal/modal';
import { WalletTopUp } from 'app/shared/topup';
import styles from './list.module.css';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { useTenantService } from 'hooks/api/iam/tenant';
import { usePurgeService } from 'hooks/api/purge';
import { SubscriptionAssignment } from '../components/subscription/assign';
import { RetentionConfiguration } from '../components/retention';
import { filter_config } from './config/filter';
import { InvoiceGeneration } from '../components/invoice';
import { useWalletService } from 'hooks/api/sales/wallet';
import { useModuleService } from 'hooks/api/sales/module';

const VIEWS = [
  { path: 'list', name: 'List' },
  { path: 'board', name: 'Board' }
];

const ListTenants = () => {
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const tenants_in_store = useSelector((state) => state.tenants);
  const [active_view, setActiveView] = useState('list');

  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { fetchModules } = useModuleService();
  const { purgeAccount } = usePurgeService();
  const { fetchTenants, searchTenants, updateTenant } = useTenantService();
  const { fetchWallets } = useWalletService();

  const [items, setItems] = useState([]);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [module_options, setModuleOptions] = useState([]);
  const [tenant_count, setTenantCount] = useState(0);

  const [selected_tenant, setSelectedTenant] = useState({});
  const [selected_tenant_id, setSelectedTenantId] = useState(0);
  const [show_topup_modal, setShowTopUpModal] = useState(false);
  const [show_assignment_modal, setShowAssignmentModal] = useState(false);
  const [show_invoice_modal, setShowInvoiceModal] = useState(false);
  const [show_retention_modal, setShowRetentionModal] = useState(false);
  const [show_tenant_delete_modal, setShowTenantDeleteModal] = useState(false);
  const [show_mark_internal_modal, setShowMarkInternalModal] = useState(false);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Tenants', path: '.' }]));

    fetchModules().then(({ modules }) => {
      const options = modules.map((mod) => ({
        label: mod.title,
        value: mod.code
      }));
      setModuleOptions(options);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sortedItems = Object.values(tenants_in_store).sort((a, b) => {
      return new Date(b.created_on) - new Date(a.created_on);
    });
    setItems(sortedItems);
  }, [tenants_in_store]);

  const handleDatatableAction = (action) => {
    const { name, type, data } = action;

    if (type.toLowerCase() === 'single') {
      switch (name.toLowerCase()) {
        case 'assign_subscription':
          setSelectedTenantId(() => data.id);
          setShowAssignmentModal(true);
          break;
        case 'retention_config':
          setSelectedTenantId(() => data.id);
          setShowRetentionModal(() => true);
          break;
        case 'generate_invoice':
          setSelectedTenant(() => data);
          setSelectedTenantId(() => data.id);
          setShowInvoiceModal(true);
          break;
        case 'edit':
          navigateTo(`/tenants/${data.id}/edit`);
          break;

        case 'top up':
          setSelectedTenantId(() => data.id);
          setShowTopUpModal(true);
          break;

        case 'delete':
          setSelectedTenantId(() => data.id);
          setShowTenantDeleteModal(true);
          break;

        case 'mark_internal':
          setSelectedTenant(() => data);
          setSelectedTenantId(() => data.id);
          setShowMarkInternalModal(true);
          break;
        default:
          navigateTo(`/tenants/${data.id}/dashboard`);
      }
    }
  };

  const handleItemClick = (data) => {
    navigateTo(`/tenants/${data.id}/dashboard`);
  };

  const handleDataRequest = async (
    page,
    population = 10,
    options = { filter_query: '', sort_query: '', reset: false }
  ) => {
    try {
      setLoading(true);
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const query_parts = [sort_query, `page=${page}`, `population=${population}`];

      if (options.filter_query && options.filter_query !== '' && options.filter_query !== '&') {
        query_parts.unshift(options.filter_query);
      }

      const query_string = query_parts.join('&');

      const { tenants, size } = await fetchTenants({ query_string });
      setTenantCount(size);

      const tenant_ids = tenants.map((tenant) => tenant.id);
      const { wallets } = await fetchWallets({
        query_string: `tenant_id=${tenant_ids.join(',')}&return_only=email,tenant_id,sms,push`
      });
      const wallet_by_tenant_id = wallets.reduce(
        (sac, item) => ({
          ...sac,
          [item.tenant_id]: {
            email: item.email,
            push: item.push,
            sms: item.sms
          }
        }),
        {}
      );

      dispatch(
        loadTenantsToStore(
          tenants.map((tenant) => ({
            ...tenant,
            ...wallet_by_tenant_id[tenant.id]
          }))
        )
      );
    } catch (e) {
      dispatch(loadTenantsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 10,
    options = { filter_query: '', sort_query: '', reset: false }
  ) => {
    if (!keys) return;
    try {
      setLoading(true);
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const query_parts = [sort_query, `page=${page}`, `population=${population}`];

      if (options.filter_query && options.filter_query !== '' && options.filter_query !== '&') {
        query_parts.unshift(options.filter_query);
      }

      const query_string = query_parts.join('&');

      const { tenants, size } = await searchTenants(['name', 'emails'], keyword, { query_string });
      const tenant_ids = tenants.map((tenant) => tenant.id);
      const { wallets } = await fetchWallets({
        query_string: `tenant_id=${tenant_ids.join(',')}&return_only=email,tenant_id,sms,push`
      });
      const wallet_by_tenant_id = wallets.reduce(
        (sac, item) => ({
          ...sac,
          [item.tenant_id]: {
            email: item.email,
            push: item.push,
            sms: item.sms
          }
        }),
        {}
      );

      const parsed_tenants = tenants.map((tenant) => ({
        ...tenant,
        ...wallet_by_tenant_id[tenant.id]
      }));

      setTenantCount(size);

      dispatch(loadTenantsToStore(parsed_tenants));
    } catch (e) {
      dispatch(loadTenantsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSingleDeletion = async (permitted) => {
    if (permitted) {
      const result = await purgeAccount(selected_tenant_id);

      if (result) dispatch(removeOneTenantFromStore(selected_tenant_id));
    }

    setShowTenantDeleteModal(false);
  };

  const handleMarkAsInternal = async (permitted) => {
    if (permitted) {
      const metadata = { ...selected_tenant.metadata, is_internal: true };
      const result = await updateTenant(selected_tenant_id, { data: { metadata } });

      if (result) dispatch(addOneTenantToStore({ ...selected_tenant, metadata }));
    }

    setShowMarkInternalModal(false);
  };

  return (
    <div>
      <DashboardSlidingNav nav_items={VIEWS} activeTab={active_view} onTabChange={setActiveView} />

      <Spacer multiple={4} />

      {active_view === 'board' && <TenantBoard />}

      {active_view === 'list' &&
        (is_mobile_view ? (
          <MobileDatatable
            config={{
              ...table_config,
              is_search_mode,
              items,
              total_count: tenant_count
            }}
            action={handleDatatableAction}
            onClick={handleItemClick}
            onListModeChange={setSearchMode}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
          />
        ) : (
          <div>
            <WebDataTable
              config={{
                ...table_config,
                is_search_mode,
                items,
                total_count: tenant_count
              }}
              filter_config={filter_config({ module_options })}
              has_filter
              action={handleDatatableAction}
              onClick={handleItemClick}
              checkbox
              loading_data={loading}
              onDataRequest={handleDataRequest}
              onSearchRequest={handleSearchRequest}
            />
          </div>
        ))}

      <ConfirmationDialog
        title="Delete Tenant"
        message="Are you sure you want to delete this tenant?"
        callback={handleSingleDeletion}
        is_open={show_tenant_delete_modal}
      />
      <ConfirmationDialog
        title="Mark as Internal"
        message="Are you sure you want to mark this tenant as internal?"
        callback={handleMarkAsInternal}
        is_open={show_mark_internal_modal}
      />
      <GmModal
        title="Wallet Top Up"
        show_title
        show_modal={show_topup_modal}
        onClose={() => setShowTopUpModal(false)}
        bodyClassName={styles.topupModal}
      >
        <WalletTopUp tenant_id={selected_tenant_id} />
      </GmModal>
      <GmModal
        title="Assign Subscription"
        show_title
        show_modal={show_assignment_modal}
        onClose={() => setShowAssignmentModal(false)}
      >
        <SubscriptionAssignment tenant_id={selected_tenant_id} />
      </GmModal>
      <GmModal
        title="Configure Retention"
        show_title
        show_modal={show_retention_modal}
        onClose={() => setShowRetentionModal(false)}
      >
        <RetentionConfiguration tenant_id={selected_tenant_id} />
      </GmModal>
      <GmModal
        title="Generate Invoice"
        show_title
        show_modal={show_invoice_modal}
        onClose={() => setShowInvoiceModal(false)}
      >
        <InvoiceGeneration tenant={selected_tenant} />
      </GmModal>
    </div>
  );
};

export default ListTenants;
